<template>
  <ReservationDetailSkeleton v-if="state === STATE.ADDING_RESERVATION" id="adding-reservation" />
  <div v-else id="seat-walk-in">
    <ReservationSellWalkIn
      v-if="state === STATE.SEAT_WALK_IN_TABLES"
      id="seat-walk-in-tables"
      :error="ui.error"
      @cancel="$emit('cancel')"
      @seatWalkIn="seatWalkIn"
      @reserve="setState(STATE.ADD_RESERVATION_FORM)"
      @reservationPaid="$emit('reservationCreated', $event)"
    />
    <SlotPanel
      v-else-if="state === STATE.ADD_RESERVATION_FORM"
      classes="min-h-full"
      id="add-reservation-form"
    >
      <div class="mb-4">
        <span class="uppercase text-sm text-fg-secondary font-label">Tables</span>
        <span
          class="text-lg font-heading font-semibold text-fg-primary block"
          id="selected-tables-name-reservation"
        >
          {{ hasSelectedTables ? selectedTablesName : 'No tables selected' }}
        </span>
        <span class="font-light text-sm">Select more tables if required.</span>
      </div>
      <ReservationForm
        id="reservation-form"
        :error="ui.error"
        @cancel="setState(STATE.SEAT_WALK_IN_TABLES)"
        @reservationSubmitted="addReservation"
      />
    </SlotPanel>
  </div>
</template>
<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationDetailSkeleton from '@/modules/reservation/components/organisms/ReservationDetailSkeleton.vue'
import ReservationForm from '@/modules/reservation/components/organisms/ReservationForm.vue'
import ReservationSellWalkIn from '@/modules/reservation/components/organisms/ReservationSellWalkIn.vue'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { blankError, displayHandled } from '@/utils/errors'

import { FeatureFlag, ReservationCreate, ReservationId } from '@generated/types'

const STATE = {
  SEAT_WALK_IN_TABLES: 'seat-walk-in-tables',
  ADD_RESERVATION_FORM: 'add-reservation-form',
  ADDING_RESERVATION: 'add-reservation-submitting',
}

export default defineComponent({
  name: 'ReservationWalkIn',
  components: {
    ReservationDetailSkeleton,
    ReservationSellWalkIn,
    SlotPanel,
    ReservationForm,
  },
  emits: {
    cancel: () => true,
    // eslint-disable-next-line no-unused-vars
    reservationCreated: (reservationId: ReservationId) => true,
  },
  data() {
    return {
      STATE,
      state: STATE.SEAT_WALK_IN_TABLES,
      ui: {
        error: blankError(),
      },
    }
  },
  methods: {
    async seatWalkIn(numberOfGuests: number) {
      if (!this.hasSelectedTables) return
      try {
        this.setState(STATE.ADDING_RESERVATION)
        const { reservation } = await this.reservationStore.seatWalkIn(
          this.selectedPerformance!,
          this.selectedTableIds,
          numberOfGuests,
        )
        this.$emit('reservationCreated', reservation.id)
      } catch (error) {
        displayHandled(error, this.ui.error)
      } finally {
        this.setState(STATE.SEAT_WALK_IN_TABLES)
      }
    },
    setState(state: string) {
      this.state = state
    },
    async addReservation(form: ReservationCreate) {
      try {
        if (form.seating_time < 1) {
          this.ui.error.code = 'SEATING_TIME_REQUIRED'
          this.ui.error.message = 'Please select a seating time'
          return
        }
        this.setState(STATE.ADDING_RESERVATION)
        const { reservation } = await this.reservationStore.createReservation(form)
        this.$emit('reservationCreated', reservation.id)
      } catch (error) {
        displayHandled(error, this.ui.error)
      } finally {
        this.setState(STATE.SEAT_WALK_IN_TABLES)
      }
    },
  },
  computed: {
    ...mapStores(useReservationStore, useSettingStore),
    ...mapState(usePerformanceReservationStore, [
      'selectedPerformance',
      'selectedTableIds',
      'selectedTables',
      'selectedTablesName',
      'hasSelectedTables',
    ]),
    isTerminalPaymentsAllowed(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.TERMINAL)
    },
  },
})
</script>
